import { z } from 'zod'

import { type PostDto, type SocialPlatform } from '../model/contracts'

const SocialPlatformSchema = z.string().transform((p) => {
  const lp = p.toLowerCase()
  if (lp === 'twitter') return 'x' as SocialPlatform
  return p as SocialPlatform
})

const PostDtoSchema: z.ZodType<PostDto> = z.object({
  id: z.string(),
  platform: SocialPlatformSchema,
  author: z.object({
    name: z.string(),
    handle: z.string().optional(),
    id: z.string().optional(),
    verified: z.boolean().optional(),
  }),
  content: z.string(),
  timestamp: z.string(),
  url: z.string().min(1),
  metrics: z.object({
    reactions: z.number().optional().default(0),
    shares: z.number().optional().default(0),
    comments: z.number().optional().default(0),
    views: z.number().optional().default(0),
  }),
  context: z.object({
    isReply: z.boolean().default(false),
    isRetweet: z.boolean().default(false),
    threadRootId: z.string().optional(),
    quotedId: z.string().optional(),
  }),
  tags: z.array(z.string()).optional(),
  enrichmentStatus: z.enum(['pending', 'processing', 'done']).optional(),
  aiSummary: z.string().optional(),
  sentiment: z.enum(['positive', 'negative', 'neutral']).optional(),
  aiTags: z.array(z.string()).optional(),
  source: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .optional(),
})

const MetaSchema = z
  .object({
    platform: z.string().optional(),
    total: z.number().int().nonnegative().optional(),
  })
  .passthrough()

const PostsEnvelopeSchema = z
  .object({
    meta: MetaSchema.optional(),
    data: z.array(PostDtoSchema).default([]),
  })
  .passthrough()

type PostsDecoded = {
  items: PostDto[]
  total?: number
  platform?: SocialPlatform
}

export function decodePostsResponse(json: unknown): PostsDecoded {
  if (Array.isArray(json)) {
    return { items: z.array(PostDtoSchema).parse(json) }
  }

  const env = PostsEnvelopeSchema.parse(json)
  return {
    items: env.data,
    total: env.meta?.total,
    platform: env.meta?.platform as SocialPlatform | undefined,
  }
}
